import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";

// Grouping fields
const GROUP_FIELDS_ALL = [
  "kmgId", // derived (lastnikId ?? rejecId), used only for grouping/UI
  "speciesSifra",
  "statusSifra",
  "spolSifra",
  "razvojniStadijSifra",
  "priznanaPasmaId",
];

const COLLAPSED_FIELDS_BASE = [
  "lastnikId",
  "lastnikNaziv",
  "rejecId",
  "rejecNaziv",
  "priznanaPasmaSifra",
];

// -----------------------------
// Helpers
// -----------------------------
const keyVal = (v) => (v === true ? "T" : v === false ? "F" : (v ?? "NULL"));

function buildGroupKey(row, keyFields) {
  return keyFields.map((f) => keyVal(row?.[f])).join("|");
}

function addValue(values, field, v) {
  if (!values[field]) values[field] = new Set();
  values[field].add(v ?? null);
}

function normalizeBucket(r) {
  const kmgId = r?.lastnikId ?? r?.rejecId ?? null;
  const kmgNaziv = r?.lastnikNaziv ?? r?.rejecNaziv ?? null;

  // Useful for UI if you want to show both “roles involved”
  const kmgIds = Array.from(
    new Set([r?.lastnikId, r?.rejecId]) //.filter((x) => x != null))
  );
  const kmgNazivi = Array.from(
    new Set([r?.lastnikNaziv, r?.rejecNaziv]) //.filter((x) => x != null))
  );

  return { ...r, kmgId, kmgNaziv, kmgIds, kmgNazivi };
}

function mergeByFields(rows, keyFields, collapsedFields) {
  const map = new Map();

  for (const raw of rows || []) {
    const r = normalizeBucket(raw);

    const id = buildGroupKey(r, keyFields);
    const prev = map.get(id);

    if (!prev) {
      const values = {};
      for (const f of collapsedFields) addValue(values, f, r?.[f]);
      map.set(id, { ...r, id, values });
      continue; // why unexpected use of continue statement warn
    }

    const merged = { ...prev };

    merged.steviloZivali = (prev.steviloZivali || 0) + (r.steviloZivali || 0);

    merged.najnizjiKoefInbriding =
      prev.najnizjiKoefInbriding == null
        ? r.najnizjiKoefInbriding
        : r.najnizjiKoefInbriding == null
          ? prev.najnizjiKoefInbriding
          : Math.min(prev.najnizjiKoefInbriding, r.najnizjiKoefInbriding);

    merged.najvisjiKoefInbriding =
      prev.najvisjiKoefInbriding == null
        ? r.najvisjiKoefInbriding
        : r.najvisjiKoefInbriding == null
          ? prev.najvisjiKoefInbriding
          : Math.max(prev.najvisjiKoefInbriding, r.najvisjiKoefInbriding);

    // weighted avg by steviloZivali (only if avg present)
    const w1 =
      prev.povprecniKoefInbriding == null ? 0 : prev.steviloZivali || 0;
    const w2 = r.povprecniKoefInbriding == null ? 0 : r.steviloZivali || 0;
    const denom = w1 + w2;

    merged.povprecniKoefInbriding =
      denom === 0
        ? null
        : ((prev.povprecniKoefInbriding || 0) * w1 +
            (r.povprecniKoefInbriding || 0) * w2) /
          denom;

    for (const f of collapsedFields) addValue(merged.values, f, r?.[f]);

    map.set(id, merged);
  }

  // Sets -> arrays (serializable)
  return Array.from(map.values()).map((b) => {
    const values = {};
    for (const [k, set] of Object.entries(b.values || {})) {
      values[k] = Array.from(set);
    }
    return { ...b, values };
  });
}

const whereInOrNull = (g, field, values) => {
  const uniq = Array.from(new Set(values || []));
  const hasNull = uniq.some((v) => v == null);
  const nonNull = uniq.filter((v) => v != null);

  if (hasNull && nonNull.length) {
    g.or((or) => {
      or.and((brack) => brack.isNull(field));
      or.where(field, "IN", nonNull);
    });
    return;
  }
  if (hasNull) return g.and((brack) => brack.isNull(field));
  if (nonNull.length === 1) return g.where(field, "EQ", nonNull[0]);
  if (nonNull.length > 1) return g.where(field, "IN", nonNull);
};

export const fetchAnimalGroupBuckets = createAsyncThunk(
  "animalGroupBuckets/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await ApiClient.get("/api/zival/zivalaggregate/skupine"); // TODO your endpoint
      return Array.isArray(res?.data) ? res.data : [];
    } catch (e) {
      return rejectWithValue(e?.message ?? "Fetch animal group buckets failed");
    }
  }
);

const initialState = {
  bucketsRaw: [],
  loading: false,
  error: null,
};

const animalGroupBucketSlice = createSlice({
  name: "animalGroupBuckets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnimalGroupBuckets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnimalGroupBuckets.fulfilled, (state, action) => {
        state.bucketsRaw = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchAnimalGroupBuckets.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action?.payload ?? action?.error?.message ?? "Unknown error";
      });
  },
});

export default animalGroupBucketSlice.reducer;

export const selectBucketsMerged = createSelector(
  [(s) => s.animalGroupBuckets.bucketsRaw],
  (raw) => {
    const rows = (raw || []).map(normalizeBucket);

    // keep kmgId always in key (otherwise your animal filter explodes)
    const excludedSet = new Set([]);

    const keyFields = GROUP_FIELDS_ALL.filter((f) => !excludedSet.has(f));

    // everything not in key becomes “collapsed” and must be preserved in values
    const collapsedFields = Array.from(
      new Set([...COLLAPSED_FIELDS_BASE, ...GROUP_FIELDS_ALL])
    );

    return mergeByFields(rows, keyFields, collapsedFields);
  }
);

export function buildAnimalFilterFromMergedBuckets(mergedBuckets) {
  const filter = new ApiFilter();

  filter.or((ored) => {
    (mergedBuckets || []).forEach((b) => {
      ored.and((g) => {
        // Dimensions (exact if in key, otherwise use values[...] if present)
        if (b.values?.speciesSifra)
          whereInOrNull(g, "speciesSifra", b.values.speciesSifra);
        else
          b.speciesSifra
            ? g.where("speciesSifra", "EQ", b.speciesSifra)
            : g.and((brack) => brack.isNull("speciesSifra"));

        if (b.values?.spolSifra)
          whereInOrNull(g, "spolSifra", b.values.spolSifra);
        else
          b.spolSifra
            ? g.where("spolSifra", "EQ", b.spolSifra)
            : g.and((brack) => brack.isNull("spolSifra"));

        if (b.values?.razvojniStadijSifra)
          whereInOrNull(g, "razvojniStadijSifra", b.values.razvojniStadijSifra);
        else
          b.razvojniStadijSifra
            ? g.where("razvojniStadijSifra", "EQ", b.razvojniStadijSifra)
            : g.and((brack) => brack.isNull("razvojniStadijSifra"));

        if (b.values?.statusSifra)
          whereInOrNull(g, "statusSifra", b.values.statusSifra);
        else
          b.statusSifra
            ? g.where("statusSifra", "EQ", b.statusSifra)
            : g.and((brack) => brack.isNull("statusSifra"));

        if (b.values?.priznanaPasmaId)
          whereInOrNull(g, "priznanaPasmaId", b.values.priznanaPasmaId);
        else
          b.priznanaPasmaId
            ? g.where("priznanaPasmaId", "EQ", b.priznanaPasmaId)
            : g.and((brack) => brack.isNull("priznanaPasmaId"));

        if (b.values?.lastnikId)
          whereInOrNull(g, "lastnikId", b.values.lastnikId);
        if (b.values?.rejecId) whereInOrNull(g, "rejecId", b.values.rejecId);
      });
    });
  });

  return filter;
}
